import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";

const VERDICTS: { key: string; label: string }[] = [
  { key: "useful", label: "Useful" },
  { key: "not_useful", label: "Not useful" },
  { key: "incorrect", label: "Incorrect" },
  { key: "unclear", label: "Unclear" },
  { key: "needs_context", label: "Needs context" },
];

/**
 * IRONDash-only, self-scoped feedback on a page/card/report. Stores only a safe
 * target ref + short note; never source content. Re-submitting updates in place.
 */
export default function FeedbackWidget({ targetType, targetRef, label = "Was this useful?" }: { targetType: string; targetRef: string; label?: string }) {
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const mine = useQuery({
    queryKey: ["feedback-mine", targetType, targetRef],
    queryFn: () => dashApi.feedbackMine(targetType, targetRef),
  });
  const current = mine.data?.feedback?.[0]?.verdict;
  const storedNote = mine.data?.feedback?.[0]?.note ?? "";
  // Seed the note from the stored value so a verdict change re-sends it rather
  // than blanking it (the upsert would otherwise NULL the note).
  useEffect(() => {
    setNote(storedNote);
  }, [storedNote]);
  const mut = useMutation({
    mutationFn: (verdict: string) => dashApi.submitFeedback({ target_type: targetType, target_ref: targetRef, verdict, note: note || undefined }),
    onSuccess: () => mine.refetch(),
  });

  return (
    <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900/30 px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-500">{label}</span>
        {VERDICTS.map((v) => (
          <button
            key={v.key}
            onClick={() => mut.mutate(v.key)}
            disabled={mut.isPending}
            className={`rounded-full px-2 py-0.5 ${current === v.key ? "bg-sky-500/20 text-sky-200 ring-1 ring-sky-500/40" : "bg-slate-800 text-slate-400 hover:text-slate-200"} disabled:opacity-50`}
          >
            {v.label}
          </button>
        ))}
        <button onClick={() => setOpen((o) => !o)} className="text-slate-600 hover:text-slate-400">
          {open ? "hide note" : "add note"}
        </button>
        {current && <span className="text-emerald-400/70">recorded</span>}
      </div>
      {open && (
        <div className="mt-2 flex gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (no sensitive data)…"
            maxLength={500}
            className="flex-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-slate-200"
          />
          {current && (
            <button onClick={() => mut.mutate(current)} disabled={mut.isPending} className="rounded bg-sky-500/20 px-2 text-sky-200 disabled:opacity-50">save note</button>
          )}
        </div>
      )}
    </div>
  );
}
